<template>
  <v-timeline dense clipped>
    <v-timeline-item
      v-for="(interview, key) in interviews.data"
      :key="`interview-${key}`"
      :color="getColorBasedOnInterviewStatus(interview)"
      small
    >
      <v-row class="elevation-1 pa-4 py-2">
        <v-col>
          <v-row class="align-center">
            <v-col class="pa-0 col-auto">
              <v-avatar size="40" color="primary">
                <span class="white--text text-h5">{{ firstTowLettersName(interview.employee_name.name) }}</span>
              </v-avatar>
            </v-col>
            <v-col>
              <div style="font-size:11px;font-weight:700;">{{ i18n.employee }}</div>
              <strong>{{ interview.employee_name.name }}</strong>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
              <div style="font-size:11px;font-weight:700;">{{ i18n.candidate }}</div>
              <div>
                <v-chip color="light-blue darken-1" dark small label class="mr-2">
                  {{ interview.candidate.aramcco_code }}
                </v-chip>
                <v-chip color="light-blue darken-1" dark small label>
                  {{ interview.candidate.name }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
              <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_appointment }}</div>
              <div>
                <v-chip color="blue darken-1" class="mr-2" dark small>
                  {{ formatDateToCurrentLocale(interview.appointment.appointment)[0] }}
                </v-chip>
                <v-chip color="blue darken-1" dark class="mr-2" small>
                  {{ formatDateToCurrentLocale(interview.appointment.appointment)[1] }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
              <div style="font-size:11px;font-weight:700;">{{ i18n.interview_application }}</div>
              <div class="body-1">{{ interview.application }}</div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
              <div style="font-size:11px;font-weight:700;">{{ i18n.interview_information }}</div>
              <div class="body-1">{{ interview.information }}</div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
              <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_update_date }}</div>
              <div class="body-1">{{ getFormat(interview.updated_at) }}</div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3 mt-2">
              <div style="font-size:11px;font-weight:700;">{{ i18n.was_present }}</div>
              <div class="body-1">{{ interview.attended ? i18n.yes : i18n.no }}</div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3 mt-2">
              <div style="font-size:11px;font-weight:700;">{{ i18n.interview_link }}</div>
              <div class="">
                <div class="body-1">{{ interview.link }}</div>
                <div class="d-flex align-items-center mt-1">
                  <v-btn
                    dark
                    v-clipboard:copy="interview.link"
                    v-clipboard:success="clipboardSuccessHandler"
                    v-clipboard:error="clipboardErrorHandler"
                    color="indigo darken-1"
                    x-small
                    class="mr-2"
                  >
                    {{ i18n.copy }}
                    <v-icon size="14" class="mr-1">link</v-icon>
                  </v-btn>
                  <v-btn link :href="interview.link" target="_blank" dark color="blue darken-1" x-small class="mr-1">
                    {{ i18n.open }}
                    <v-icon size="14" class="mr-1">open_in_new</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3 mt-2">
              <div style="font-size:11px;font-weight:700;">{{ i18n.interview_status }}</div>
              <div>
                <v-chip small :color="getColorBasedOnInterviewStatus(interview)">
                  {{
                    !interview.accepted && !interview.rejected
                      ? i18n.interview_pending
                      : interview.accepted
                      ? i18n.interview_accepted
                      : i18n.interview_rejected
                  }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="interview.note" class="mt-md-2 ">
            <v-col cols="12" class="pa-0">
              <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_note }}</div>
              <div>
                {{ interview.note }}
              </div>
            </v-col>
          </v-row>
          <v-row v-if="interview.result" class="mt-2">
            <v-col col="12" class="pa-0">
              <div class="mb-1">
                <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_result }}</div>
              </div>
              <v-chip label small color="blue" dark>
                {{ interview.result }}
              </v-chip>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col col="12" class="pa-0">
              <v-btn color="primary" small class="mr-2" @click.prevent="$emit('editResource', interview)">
                <v-icon x-small class="mr-1">edit</v-icon>
                Edit
              </v-btn>
              <v-btn color="error" small class="mr-2" @click.prevent="$emit('deleteInterview', interview)">
                <v-icon x-small class="mr-1">delete</v-icon>
                Delete
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-timeline-item>
  </v-timeline>
</template>

<script>
import { format } from "timeago.js";

export default {
  props: ["interviews"],
  methods: {
    getColorBasedOnInterviewStatus(interview) {
      return !interview.accepted && !interview.rejected ? `warning` : interview.accepted ? "success" : "red";
    },
    firstTowLettersName(name) {
      const split_names = name.split(" ");

      if (split_names.length == 0) return "";

      if (split_names.length > 1) return (split_names[0][0] + split_names[1][0]).toUpperCase();

      return (split_names[0][0] + split_names[0][1]).toUpperCase();
    },
    formatDateToCurrentLocale(date) {
      let new_date = new Date(date);
      return [
        new_date.toLocaleDateString(this.arabic ? "ar" : "en", {
          day: "numeric",
          year: "numeric",
          month: "short",
        }),
        new_date.toLocaleTimeString(this.arabic ? "ar" : "en"),
      ];
    },
    getFormat(d) {
      return format(d);
    },
    clipboardSuccessHandler({ value, event }) {
      this.$emit("copyS", { value, event });
    },
    clipboardErrorHandler({ value, event }) {
      this.$emit("copyF", { value, event });
    },
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    language() {
      return this.$store.state.language;
    },
    i18n() {
      return this.$store.state.i18n[this.$store.state.language];
    },
    arabic() {
      if (this.$store.state.language == "ar") return true;
    },
  },
};
</script>
